'use client';

import mixpanel from 'mixpanel-browser';
import Script from 'next/script';
import posthog from 'posthog-js';
import { useEffect, useMemo, useRef } from 'react';
import { authClient } from '@/lib/auth-client';
import { trpc } from '@/lib/trpc';

function isProd() {
  return process.env.NODE_ENV === 'production';
}

// Constants for cookie management
const COOKIE_DAYS = 30;
const SECONDS_PER_DAY = 24 * 60 * 60;
const MILLISECONDS_PER_SECOND = 1000;

// Type definition for CookieStore API
type CookieStore = {
  get(name: string): Promise<{ value: string } | undefined>;
  set(options: {
    name: string;
    value: string;
    expires?: Date;
    path?: string;
    sameSite?: 'strict' | 'lax' | 'none';
  }): Promise<void>;
};

type WindowWithCookieStore = Window & {
  cookieStore?: CookieStore;
};

// Helper function to set cookie using document.cookie as fallback
const setDocumentCookie = (name: string, value: string, expires: Date): void => {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not available, using document.cookie as fallback
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

// Capture and store ttclid (TikTok Click ID) from URL parameters
async function captureTtclid(): Promise<string | null> {
  const params = new URLSearchParams(window.location.search);
  const ttclid = params.get('ttclid');

  if (ttclid) {
    // Store ttclid in a cookie for 30 days using Cookie Store API
    try {
      const expirationDate = new Date();
      expirationDate.setTime(
        expirationDate.getTime() + COOKIE_DAYS * SECONDS_PER_DAY * MILLISECONDS_PER_SECOND
      );

      const windowWithCookieStore = window as WindowWithCookieStore;
      if ('cookieStore' in window && windowWithCookieStore.cookieStore) {
        // Use Cookie Store API if available
        await windowWithCookieStore.cookieStore.set({
          name: 'ttclid',
          value: ttclid,
          expires: expirationDate,
          path: '/',
          sameSite: 'lax',
        });
      } else {
        // Fallback to document.cookie
        setDocumentCookie('ttclid', ttclid, expirationDate);
      }
      return ttclid;
    } catch (error) {
      console.error('Failed to set ttclid cookie:', error);
      return null;
    }
  }

  // Try to get existing ttclid from cookie
  try {
    const windowWithCookieStore = window as WindowWithCookieStore;
    if ('cookieStore' in window && windowWithCookieStore.cookieStore) {
      const cookies = await windowWithCookieStore.cookieStore.get('ttclid');
      return cookies?.value || null;
    }
    // Fallback to document.cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'ttclid') {
        return value;
      }
    }
  } catch (error) {
    console.error('Failed to get ttclid cookie:', error);
  }

  return null;
}

type DataLayerEvent = Record<string, unknown>;
type AnalyticsWindow = typeof window & {
  dataLayer?: DataLayerEvent[];
  fbq?: (...args: unknown[]) => void;
  ttq?: {
    load: (pixelId: string) => void;
    track: (event: string, properties?: Record<string, unknown>) => void;
  };
  tracking?: {
    track: (event: string, payload?: Record<string, unknown>) => void;
  };
  posthogInitialized?: boolean;
};

function persistFirstTouchUtm(): Record<string, string | null> {
  const params = new URLSearchParams(window.location.search);
  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'fbclid',
  ];
  const existing = localStorage.getItem('utm_first_touch');
  if (existing) {
    try {
      return JSON.parse(existing) as Record<string, string | null>;
    } catch {
      localStorage.removeItem('utm_first_touch');
    }
  }
  const utm: Record<string, string | null> = {};
  let _hasUtm = false;
  for (const key of utmKeys) {
    const value = params.get(key);
    utm[key] = value;
    if (value) {
      _hasUtm = true;
    }
  }
  localStorage.setItem('utm_first_touch', JSON.stringify(utm));
  return utm;
}

function getStoredUtm(): Record<string, string | null> {
  const stored = localStorage.getItem('utm_first_touch');
  if (!stored) {
    return {};
  }
  try {
    return JSON.parse(stored) as Record<string, string | null>;
  } catch {
    localStorage.removeItem('utm_first_touch');
    return {};
  }
}

export default function AnalyticsLoader({ nonce }: { nonce?: string }) {
  const { data: session } = authClient.useSession();
  const { data } = trpc.config.getTracking.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const enabled =
    isProd() && data && (data.gtmId || data.mixpanelToken || data.clarityId || data.tiktokPixelId);
  const trackedRef = useRef(false);

  const mixpanelDistinctId = useMemo(() => {
    const maybeId = session?.user?.id;
    return typeof maybeId === 'string' && maybeId.length > 0 ? maybeId : undefined;
  }, [session?.user?.id]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const initializeAnalytics = async () => {
      const w = window as AnalyticsWindow;
      const utm = persistFirstTouchUtm();
      const ttclid = await captureTtclid();
      const utmContext = Object.keys(utm).reduce<Record<string, string>>((acc, key) => {
        const value = utm[key];
        if (value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      const baseEvent = {
        event: 'page_view',
        page_path: w.location.pathname,
        page_location: w.location.href,
        page_referrer: document.referrer || undefined,
        ...utmContext,
      };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      w.dataLayer.push(baseEvent);
      if (w.fbq) {
        w.fbq('track', 'PageView', utmContext);
      }
      if (w.ttq && data?.tiktokPixelId) {
        w.ttq.track('PageView', { ...utmContext, ttclid });
      }
      w.tracking = {
        track: (event: string, payload?: Record<string, unknown>) => {
          const attrs = payload ?? {};
          w.dataLayer = w.dataLayer || [];
          w.dataLayer.push({ event, ...attrs });
          if (w.fbq) {
            w.fbq('trackCustom', event, attrs);
          }
          if (w.ttq && data?.tiktokPixelId) {
            w.ttq.track(event, attrs);
          }
          mixpanel.track(event, attrs);
          posthog.capture(event, attrs);
        },
      };
      if (data?.mixpanelToken && typeof data.mixpanelToken === 'string') {
        try {
          mixpanel.init(data.mixpanelToken, {
            debug: false,
            api_host: 'https://api.mixpanel.com',
            ignore_dnt: false,
          });
          if (mixpanelDistinctId) {
            mixpanel.identify(mixpanelDistinctId);
          }
          if (!trackedRef.current) {
            const storedUtm = getStoredUtm();
            const props: Record<string, unknown> = {
              path: window.location.pathname,
              referrer: document.referrer || undefined,
              ...storedUtm,
            };
            mixpanel.track('page_view', props);
            (window as Window & { __mp_page_view_tracked?: boolean }).__mp_page_view_tracked = true;
            trackedRef.current = true;
          }
        } catch (_e) {
          // mixpanel init failed; non-fatal
        }
      }
      // Initialize PostHog once; the tracking effect can rerun when auth data changes.
      const PH_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      const PH_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
      if (PH_KEY && !w.posthogInitialized) {
        try {
          posthog.init(PH_KEY, {
            api_host: PH_HOST,
            capture_pageview: false,
            capture_pageleave: true,
            autocapture: false,
            persistence: 'localStorage',
            disable_session_recording: false,
            loaded: (ph) => {
              if (mixpanelDistinctId) {
                ph.identify(mixpanelDistinctId);
              }
              if (!trackedRef.current) {
                const storedUtm = getStoredUtm();
                const props: Record<string, unknown> = {
                  path: window.location.pathname,
                  referrer: document.referrer || undefined,
                  ...storedUtm,
                };
                ph.capture('page_view', props);
              }
            },
          });
          w.posthogInitialized = true;
        } catch {
          // posthog init failed; non-fatal
        }
      }
    };

    initializeAnalytics();
  }, [enabled, data?.mixpanelToken, data?.tiktokPixelId, mixpanelDistinctId]);

  // Render direct analytics scripts conditionally. GTM is intentionally omitted:
  // its injected inline tags cannot satisfy the strict per-request CSP nonce.
  // Clarity, TikTok, Facebook, Mixpanel, and PostHog remain direct integrations.
  if (!enabled) {
    return null;
  }

  return (
    <>
      {/* Direct analytics integrations use the nonce above. */}

      {/* Facebook Pixel fallback (redundant with GTM to guarantee firing) */}
      {data?.fbPixelId ? (
        <>
          <Script id="fb-pixel" nonce={nonce} strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
              n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${data.fbPixelId}');
              fbq('track', 'PageView');
            `}
          </Script>
          <noscript>
            {/*
              biome-ignore lint/performance/noImgElement: Using img element instead of Next.js Image because this is in a noscript tag
              which only renders when JavaScript is disabled, so Next.js components cannot be used here.
              This is required for Facebook Pixel noscript fallback.
            */}
            <img
              alt=""
              height="1"
              src={`https://www.facebook.com/tr?id=${data.fbPixelId}&ev=PageView&noscript=1`}
              style={{ display: 'none' }}
              width="1"
            />
          </noscript>
        </>
      ) : null}

      {/* Microsoft Clarity */}
      {data?.clarityId ? (
        <Script id="microsoft-clarity-init" nonce={nonce} strategy="afterInteractive">
          {`
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${data.clarityId}");
          `}
        </Script>
      ) : null}

      {/* TikTok Pixel */}
      {data?.tiktokPixelId ? (
        <Script id="tiktok-pixel" nonce={nonce} strategy="afterInteractive">
          {`
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
              ttq.load('${data.tiktokPixelId}');
              ttq.page();
            }(window, document, 'ttq');
          `}
        </Script>
      ) : null}
    </>
  );
}
